import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  MessageInlineImagesListDocument,
  MessageInlineImagesListQuery,
  MessageInlineImagesListQueryVariables,
} from 'core/graphql';

import { hasCidImages, IInlineImage, resolveCidImages } from './richMailModel';

/**
 * Адрес картинки живёт час; ответ держим в памяти 45 минут — переоткрытие
 * письма, прокрутка переписки и повторный монтаж не перезапрашивают адреса.
 */
export const INLINE_IMAGES_CACHE_MS = 45 * 60 * 1000;

/**
 * Не чаще раза в минуту на письмо перезапрашиваем адреса по ошибке
 * картинки: действительно битая картинка не заводит цикл «ошибка →
 * запрос → перерисовка фрейма → ошибка».
 */
export const INLINE_IMAGES_RETRY_MS = 60 * 1000;

/** Сколько писем держим в памяти (старейшие вытесняются). */
const CACHE_LIMIT = 300;

type InlineImagesFetcher = (messageId: string) => Promise<IInlineImage[]>;

interface ICacheEntry {
  images: IInlineImage[];
  at: number;
}

const cache = new Map<string, ICacheEntry>();
const inflight = new Map<string, Promise<IInlineImage[]>>();
const lastRetry = new Map<string, number>();

/** Для тестов: забыть кеш, запросы в полёте и отметки перезапросов. */
export const resetInlineImagesCache = (): void => {
  cache.clear();
  inflight.clear();
  lastRetry.clear();
};

const remember = (messageId: string, images: IInlineImage[], at: number) => {
  cache.delete(messageId);
  cache.set(messageId, { images, at });
  if (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
};

/** Свежий (моложе {@link INLINE_IMAGES_CACHE_MS}) ответ из памяти. */
export const peekInlineImages = (
  messageId: string,
  now: number = Date.now(),
): IInlineImage[] | null => {
  const entry = cache.get(messageId);
  return entry && now - entry.at < INLINE_IMAGES_CACHE_MS ? entry.images : null;
};

/**
 * Адреса картинок письма с дедупликацией: один запрос в полёте на письмо,
 * свежий ответ — из памяти (`force` — мимо памяти, но не мимо запроса в
 * полёте). Перезапрос не затирает рабочие адреса: ошибка или пустой ответ
 * при уже известных картинках возвращает прежние. Ошибка без прежних —
 * отказ промиса.
 */
export const loadInlineImages = (
  fetcher: InlineImagesFetcher,
  messageId: string,
  force = false,
): Promise<IInlineImage[]> => {
  const fresh = force ? null : peekInlineImages(messageId);
  if (fresh) return Promise.resolve(fresh);
  const pending = inflight.get(messageId);
  if (pending) return pending;
  const request = fetcher(messageId)
    .then(
      (images) => {
        const previous = cache.get(messageId);
        if (images.length === 0 && previous?.images.length) {
          return previous.images;
        }
        remember(messageId, images, Date.now());
        return images;
      },
      (error) => {
        const previous = cache.get(messageId);
        if (previous?.images.length) return previous.images;
        throw error;
      },
    )
    .finally(() => {
      inflight.delete(messageId);
    });
  inflight.set(messageId, request);
  return request;
};

/**
 * Можно ли сейчас перезапросить адреса письма по ошибке картинки (не чаще
 * {@link INLINE_IMAGES_RETRY_MS}); `true` — и отметка поставлена.
 */
export const claimInlineImagesRetry = (
  messageId: string,
  now: number = Date.now(),
): boolean => {
  const last = lastRetry.get(messageId);
  if (last !== undefined && now - last < INLINE_IMAGES_RETRY_MS) return false;
  lastRetry.set(messageId, now);
  return true;
};

interface IInlineImagesState {
  messageId: string;
  images: IInlineImage[];
}

/**
 * Картинки `cid:` письма — подписанные адреса приватного S3 (живут час).
 *
 * Запрос `no-cache` мимо Apollo (мёртвые адреса не должны всплыть на другом
 * экране), ответ — в памяти модуля на 45 минут и в локальном состоянии.
 * По таймеру не перезапрашиваем: srcDoc фрейма стабилен, уже показанные
 * картинки не перегружаются. Перезапрос — при монтаже после истечения
 * памяти или по `refresh()` (картинка во фрейме не загрузилась — адрес
 * истёк), не чаще раза в минуту. Ошибка или пустой ответ перезапроса не
 * стирают прежние адреса; ошибка первого запроса — пустой список (письмо с
 * пустыми местами вместо картинок).
 */
export const useMessageInlineImages = (
  messageId: string | null | undefined,
  enabled: boolean,
) => {
  const client = useApolloClient();
  const [state, setState] = useState<IInlineImagesState | null>(null);
  const [loading, setLoading] = useState(false);
  const [round, setRound] = useState(0);
  const forceNext = useRef(false);

  const fetcher = useCallback<InlineImagesFetcher>(
    (id) =>
      client
        .query<
          MessageInlineImagesListQuery,
          MessageInlineImagesListQueryVariables
        >({
          query: MessageInlineImagesListDocument,
          variables: { messageId: id },
          fetchPolicy: 'no-cache',
        })
        .then(({ data }) =>
          (data?.messageInlineImages || []).map(({ cid, url }) => ({
            cid,
            url,
          })),
        ),
    [client],
  );

  useEffect(() => {
    if (!enabled || !messageId) return undefined;
    let alive = true;
    const force = forceNext.current;
    forceNext.current = false;
    setLoading(true);
    loadInlineImages(fetcher, messageId, force)
      .then((images) => {
        if (!alive) return;
        setState((prev) =>
          prev?.messageId === messageId &&
          prev.images.length > 0 &&
          images.length === 0
            ? prev
            : { messageId, images },
        );
      })
      .catch(() => {
        if (!alive) return;
        setState((prev) =>
          prev?.messageId === messageId ? prev : { messageId, images: [] },
        );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [fetcher, messageId, enabled, round]);

  /** Картинка не загрузилась — один перезапрос адресов (не чаще раза в минуту). */
  const refresh = useCallback(() => {
    if (!enabled || !messageId || !claimInlineImagesRetry(messageId)) return;
    forceNext.current = true;
    setRound((value) => value + 1);
  }, [enabled, messageId]);

  // Ответ по прошлому письму к этому не относится; свежая память — сразу,
  // без кадра с пустыми местами при повторном монтаже.
  const images =
    state && state.messageId === messageId
      ? state.images
      : (messageId && peekInlineImages(messageId)) || null;
  return { images, loading, refresh };
};

/**
 * HTML письма с подставленными картинками `cid:`. Пока адреса не пришли —
 * прозрачные пиксели на их местах (текст письма виден сразу).
 * `onImageError` — отдать фрейму: картинка не загрузилась → перезапрос.
 */
export const useEmailHtmlWithInlineImages = (
  messageId: string | null | undefined,
  bodyHtml: string,
): { html: string; onImageError?: () => void } => {
  const needsImages = useMemo(() => hasCidImages(bodyHtml), [bodyHtml]);
  const { images, refresh } = useMessageInlineImages(messageId, needsImages);
  const html = useMemo(
    () => (needsImages ? resolveCidImages(bodyHtml, images) : bodyHtml),
    [bodyHtml, images, needsImages],
  );
  return { html, onImageError: needsImages ? refresh : undefined };
};
